import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Pencil, Trash2, UserCheck, Ban, ShieldCheck, Search, CheckCircle2, Clock, XCircle, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getStatusBadge = (status) => {
  const map = {
    active:  { label: 'Actif',      cls: 'bg-green-100 text-green-700', icon: <CheckCircle2 className="w-3 h-3" /> },
    pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-700', icon: <Clock className="w-3 h-3" /> },
    banned:  { label: 'Banni',      cls: 'bg-red-100 text-red-700',     icon: <XCircle className="w-3 h-3" /> },
  };
  const s = map[status] || map.active;
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 w-fit ${s.cls}`}>
      {s.icon} {s.label}
    </span>
  );
};

// MembersTab — list + filter + approve/ban/unban/edit/delete
const MembersTab = ({ onPendingCountChange }) => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [profileTypes, setProfileTypes] = useState([]);
  const [memberFilter, setMemberFilter] = useState('all');
  const [memberSearch, setMemberSearch] = useState('');

  const [showBanDialog, setShowBanDialog] = useState(false);
  const [showEditMemberDialog, setShowEditMemberDialog] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [editMemberForm, setEditMemberForm] = useState({});

  const fetchMembers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (memberFilter !== 'all') params.append('status', memberFilter);
      if (memberSearch) params.append('search', memberSearch);
      const res = await axios.get(`${API}/admin/members?${params.toString()}`, { withCredentials: true });
      setMembers(res.data);
      if (onPendingCountChange) {
        onPendingCountChange(res.data.filter(m => m.status === 'pending').length);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  }, [memberFilter, memberSearch, onPendingCountChange]);

  useEffect(() => {
    fetchMembers();
    axios.get(`${API}/admin/profile-types`, { withCredentials: true })
      .then(r => setProfileTypes(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberFilter, memberSearch]);

  const handleApproveMember = async (userId) => {
    try {
      await axios.put(`${API}/admin/members/${userId}/approve`, {}, { withCredentials: true });
      toast.success('Membre approuvé');
      fetchMembers();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleBanMember = async () => {
    if (!selectedMember) return;
    try {
      await axios.put(`${API}/admin/members/${selectedMember.user_id}/ban`,
        { reason: banReason }, { withCredentials: true });
      toast.success('Membre banni');
      setShowBanDialog(false);
      setBanReason('');
      setSelectedMember(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUnbanMember = async (userId) => {
    try {
      await axios.put(`${API}/admin/members/${userId}/unban`, {}, { withCredentials: true });
      toast.success('Membre réactivé');
      fetchMembers();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleDeleteMember = async (userId) => {
    if (!window.confirm('Supprimer définitivement ce membre ?')) return;
    try {
      await axios.delete(`${API}/admin/members/${userId}`, { withCredentials: true });
      toast.success('Membre supprimé');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdateMember = async () => {
    if (!selectedMember) return;
    try {
      await axios.put(`${API}/admin/members/${selectedMember.user_id}`, editMemberForm, { withCredentials: true });
      toast.success('Membre mis à jour');
      setShowEditMemberDialog(false);
      fetchMembers();
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:flex-1">
          <div className="relative w-full sm:flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="member-search"
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              placeholder="Rechercher un membre..."
              className="pl-10"
            />
          </div>
          <Select value={memberFilter} onValueChange={setMemberFilter}>
            <SelectTrigger className="w-[160px]" data-testid="member-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="active">Actifs</SelectItem>
              <SelectItem value="banned">Bannis</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm text-gray-500">{members.length} membre(s)</div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[700px]" data-testid="members-table">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Membre</th>
              <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Profil</th>
              <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Société</th>
              <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
              <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Jetons</th>
              <th className="text-right p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map(member => (
              <tr key={member.user_id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    {member.picture ? (
                      <img src={member.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <span className="text-gray-600 text-xs font-medium">{member.name?.charAt(0)}</span>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {member.name}
                        {member.is_admin && <ShieldCheck className="w-3.5 h-3.5 text-blue-600 inline ml-1" />}
                      </p>
                      <p className="text-xs text-gray-500">{member.email}</p>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <span className="text-sm text-gray-700">{member.profile_type || '-'}</span>
                </td>
                <td className="p-3">
                  <span className="text-sm text-gray-700">{member.company_name || '-'}</span>
                </td>
                <td className="p-3">{getStatusBadge(member.status || 'active')}</td>
                <td className="p-3">
                  <span className="text-sm font-medium text-gray-700">{member.tokens ?? 0}</span>
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    {(member.status === 'pending' || !member.status) && (
                      <Button
                        variant="ghost" size="icon"
                        className="text-green-600 hover:bg-green-50 h-8 w-8"
                        onClick={() => handleApproveMember(member.user_id)}
                        title="Approuver"
                        data-testid={`approve-${member.user_id}`}
                      >
                        <UserCheck className="w-4 h-4" />
                      </Button>
                    )}
                    {member.status === 'banned' && (
                      <Button
                        variant="ghost" size="icon"
                        className="text-blue-600 hover:bg-blue-50 h-8 w-8"
                        onClick={() => handleUnbanMember(member.user_id)}
                        title="Réactiver"
                        data-testid={`unban-${member.user_id}`}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setSelectedMember(member);
                        setEditMemberForm({
                          first_name: member.first_name || '',
                          last_name: member.last_name || '',
                          phone: member.phone || '',
                          profile_type: member.profile_type || '',
                          tokens: member.tokens || 0,
                          is_admin: member.is_admin || false
                        });
                        setShowEditMemberDialog(true);
                      }}
                      title="Modifier"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {member.status !== 'banned' && member.user_id !== user.user_id && (
                      <Button
                        variant="ghost" size="icon"
                        className="text-orange-600 hover:bg-orange-50 h-8 w-8"
                        onClick={() => { setSelectedMember(member); setBanReason(''); setShowBanDialog(true); }}
                        title="Bannir"
                        data-testid={`ban-${member.user_id}`}
                      >
                        <Ban className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {member.user_id !== user.user_id && (
                      <Button
                        variant="ghost" size="icon"
                        className="text-red-600 hover:bg-red-50 h-8 w-8"
                        onClick={() => handleDeleteMember(member.user_id)}
                        title="Supprimer"
                        data-testid={`delete-${member.user_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {members.length === 0 && (
          <div className="text-center py-12 text-gray-500">Aucun membre trouvé</div>
        )}
      </div>

      <Dialog open={showBanDialog} onOpenChange={setShowBanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bannir le membre</DialogTitle>
            <DialogDescription>
              {selectedMember?.name} ({selectedMember?.email}) sera bloqué jusqu'à examen de son cas.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-gray-700 block mb-1.5">Raison du bannissement</label>
            <Input
              data-testid="ban-reason-input"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              placeholder="Ex: Utilisation abusive du service"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBanDialog(false)}>Annuler</Button>
            <Button onClick={handleBanMember} className="bg-red-600 hover:bg-red-700" data-testid="confirm-ban-btn">
              <Ban className="w-4 h-4 mr-2" />
              Bannir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditMemberDialog} onOpenChange={setShowEditMemberDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le membre</DialogTitle>
            <DialogDescription>{selectedMember?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">Prénom</label>
                <Input value={editMemberForm.first_name || ''} onChange={(e) => setEditMemberForm({...editMemberForm, first_name: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">Nom</label>
                <Input value={editMemberForm.last_name || ''} onChange={(e) => setEditMemberForm({...editMemberForm, last_name: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Type de profil</label>
              <Select value={editMemberForm.profile_type || 'none'} onValueChange={(v) => setEditMemberForm({...editMemberForm, profile_type: v === 'none' ? null : v})}>
                <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non défini</SelectItem>
                  {profileTypes.map(t => (
                    <SelectItem key={t.type_id} value={t.name}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">Jetons</label>
                <Input type="number" value={editMemberForm.tokens || 0} onChange={(e) => setEditMemberForm({...editMemberForm, tokens: parseInt(e.target.value) || 0})} />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editMemberForm.is_admin || false}
                    onChange={(e) => setEditMemberForm({...editMemberForm, is_admin: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Administrateur</span>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditMemberDialog(false)}>Annuler</Button>
            <Button onClick={handleUpdateMember} className="bg-blue-600 hover:bg-blue-700">Sauvegarder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MembersTab;
